import {
  LESSON_VIEWER_FETCH_START,
  LESSON_VIEWER_FETCH_SUCCESS,
  LESSON_VIEWER_FETCH_ERROR,
  LESSON_VIEWER_RESET,
  LESSON_VIEWER_NARRATION_PENDING,
  LESSON_VIEWER_NARRATION_PLAYING,
  LESSON_VIEWER_NARRATION_COMPLETE,
  LESSON_VIEWER_NARRATION_ERROR,
  LESSON_VIEWER_NARRATION_RESET,
  LESSON_VIEWER_SET_PHASE,
  LESSON_VIEWER_ADVANCE_TERM,
  LESSON_VIEWER_SET_QUESTION_INDEX,
  LESSON_VIEWER_SET_LEARNING_STEP_INDEX,
  LESSON_VIEWER_SET_WORKBOOK_EXERCISE_INDEX,
  LESSON_VIEWER_WORKBOOK_READY,
  LESSON_VIEWER_WORKBOOK_DONE,
  LESSON_VIEWER_WORKBOOK_SUBMIT,
  LESSON_VIEWER_KC_SUBMIT,
  LESSON_VIEWER_KC_NEXT,
  LESSON_VIEWER_KC_SUMMARY_DONE,
  LESSON_VIEWER_MASTERY_SUBMIT,
  LESSON_VIEWER_MASTERY_NEXT,
  LESSON_VIEWER_START_TERM_REVIEW,
  LESSON_VIEWER_RETAKE_MASTERY,
  LESSON_VIEWER_TOGGLE_DEV_TOOLS,
  LESSON_VIEWER_DEV_JUMP,
  LESSON_VIEWER_SET_TUTORIAL_VIEWED,
  LESSON_VIEWER_SET_TUTOR_CHAT_OPEN,
  LESSON_VIEWER_UNITY_WORLD_LOADING_SET,
  LESSON_VIEWER_KEY_TERM_PROGRESS_SET,
} from './actionTypes';
import {isDevelopmentEnvironment} from 'utils/environment';

const WORKBOOK_RESET_FIELDS = {
  workbookReady: false,
  workbookDone: false,
  workbookSubmitted: false,
  workbookSelectedOption: null,
};

const TERM_RESET_FIELDS = {
  ...WORKBOOK_RESET_FIELDS,
  kcAnswers: [],
  kcComplete: false,
  activeQuestionIndex: 0,
  activeLearningStepIndex: 0,
  activeWorkbookExerciseIndex: 0,
};

const INITIAL_SESSION_STATS = {
  answersTotal: 0,
  answersCorrect: 0,
  answerTimestamps: [],
};

function appendSessionAnswer(stats, correct, timestamp) {
  return {
    answersTotal: stats.answersTotal + 1,
    answersCorrect: stats.answersCorrect + (correct ? 1 : 0),
    answerTimestamps: [...stats.answerTimestamps, timestamp],
  };
}

export const initialState = {
  loading: false,
  error: null,
  rawPayload: null,
  viewerModel: null,
  narrationStatus: 'idle',
  narrationAudioId: null,
  narrationError: null,

  phase: 'tutorial',
  activeTermIndex: 0,
  activeQuestionIndex: 0,
  activeLearningStepIndex: 0,
  activeWorkbookExerciseIndex: 0,

  workbookReady: false,
  workbookDone: false,
  workbookSubmitted: false,
  workbookSelectedOption: null,

  kcAnswers: [],
  kcComplete: false,

  masteryAnswers: [],
  masteryComplete: false,
  returnToResultsAfterReview: false,
  sessionStats: INITIAL_SESSION_STATS,

  showDevTools: isDevelopmentEnvironment,
  tutorialViewed: false,
  tutorChatOpen: false,

  /** Web only: true until Unity legacy `x` + bridge init batch (lessonViewerUnityBridge). */
  unityWorldLoading: false,

  /**
   * Persistent per-key-term progress map keyed by term name (lowercased).
   * Populated from GET /key-term-progress on lesson load. Drives the
   * progress tracker's pre-checked LEARN/WORK/CHECK boxes for terms the
   * learner has already cleared on a previous session, and gates which
   * tracker steps are clickable.
   *   value: 'not_started' | 'learning' | 'workbook' | 'knowledge_check' | 'completed'
   */
  keyTermProgress: {},
};

const reducer = (state = initialState, action) => {
  switch (action.type) {
    case LESSON_VIEWER_FETCH_START:
      return {...state, loading: true, error: null};

    case LESSON_VIEWER_FETCH_SUCCESS:
      return {
        ...initialState,
        showDevTools: state.showDevTools,
        loading: false,
        rawPayload: action.payload.rawPayload,
        viewerModel: action.payload.viewerModel,
        tutorialViewed: action.payload.tutorialViewed || false,
        phase: 'frontloading',
        unityWorldLoading: Boolean(action.payload.unityWorldLoading),
        keyTermProgress: state.keyTermProgress,
      };

    case LESSON_VIEWER_FETCH_ERROR:
      return {...state, loading: false, error: action.payload};

    case LESSON_VIEWER_NARRATION_PENDING:
      return {
        ...state,
        narrationStatus: 'pending',
        narrationAudioId: action.payload.audioId || null,
        narrationError: null,
      };

    case LESSON_VIEWER_NARRATION_PLAYING:
      return {
        ...state,
        narrationStatus: 'playing',
        narrationAudioId: action.payload.audioId || state.narrationAudioId,
        narrationError: null,
      };

    case LESSON_VIEWER_NARRATION_COMPLETE:
      return {
        ...state,
        narrationStatus: 'complete',
        narrationAudioId: action.payload.audioId || state.narrationAudioId,
        narrationError: null,
      };

    case LESSON_VIEWER_NARRATION_ERROR:
      return {
        ...state,
        narrationStatus: 'error',
        narrationAudioId: action.payload.audioId || state.narrationAudioId,
        narrationError: action.payload.error || null,
      };

    case LESSON_VIEWER_NARRATION_RESET:
      return {
        ...state,
        narrationStatus: 'idle',
        narrationAudioId: null,
        narrationError: null,
      };

    case LESSON_VIEWER_RESET:
      return {...initialState};

    case LESSON_VIEWER_SET_PHASE:
      return {...state, phase: action.payload, tutorChatOpen: false};

    case LESSON_VIEWER_ADVANCE_TERM:
      return {
        ...state,
        activeTermIndex: action.payload,
        phase: 'learning',
        ...TERM_RESET_FIELDS,
        returnToResultsAfterReview: false,
        tutorChatOpen: false,
      };

    case LESSON_VIEWER_SET_QUESTION_INDEX:
      return {...state, activeQuestionIndex: action.payload};

    case LESSON_VIEWER_SET_LEARNING_STEP_INDEX:
      return {
        ...state,
        activeLearningStepIndex: action.payload,
        tutorChatOpen: false,
      };

    case LESSON_VIEWER_SET_WORKBOOK_EXERCISE_INDEX:
      return {
        ...state,
        activeWorkbookExerciseIndex: action.payload,
        ...WORKBOOK_RESET_FIELDS,
        tutorChatOpen: false,
      };

    case LESSON_VIEWER_WORKBOOK_READY:
      return {...state, workbookReady: true};

    case LESSON_VIEWER_WORKBOOK_DONE:
      return {...state, workbookDone: true, phase: 'workbook_options'};

    case LESSON_VIEWER_WORKBOOK_SUBMIT:
      return {
        ...state,
        workbookSubmitted: true,
        workbookSelectedOption: action.payload.optionIndex,
        sessionStats: appendSessionAnswer(
          state.sessionStats,
          action.payload.correct,
          action.payload.timestamp,
        ),
      };

    case LESSON_VIEWER_KC_SUBMIT:
      return {
        ...state,
        kcAnswers: [
          ...state.kcAnswers,
          {
            questionIndex: action.payload.questionIndex,
            selectedAnswer: action.payload.selectedAnswer,
            correct: action.payload.correct,
          },
        ],
        sessionStats: appendSessionAnswer(
          state.sessionStats,
          action.payload.correct,
          action.payload.timestamp,
        ),
      };

    case LESSON_VIEWER_KC_NEXT:
      return {
        ...state,
        activeQuestionIndex: state.activeQuestionIndex + 1,
      };

    case LESSON_VIEWER_KC_SUMMARY_DONE: {
      if (state.returnToResultsAfterReview) {
        return {
          ...state,
          phase: 'results',
          returnToResultsAfterReview: false,
          tutorChatOpen: false,
        };
      }

      // Find the next term that hasn't been fully cleared yet, rather than
      // blindly advancing `activeTermIndex + 1`. This matters when the
      // learner jumped ahead via the progress tracker — e.g. completed
      // KT1+KT2, jumped to KT5, finished KT5 — and KT3/KT4 are still
      // unfinished. We want to send them back to the first incomplete
      // term, not skip straight to the (still-locked) mastery test.
      const terms = state.viewerModel?.terms || [];
      const totalTerms = terms.length;
      const keyTermProgress = state.keyTermProgress || {};
      const isTermCompleted = term => {
        const key = String(term?.term || '').trim().toLowerCase();
        return keyTermProgress[key] === 'completed';
      };

      // Prefer terms after the current one (so a learner finishing the
      // last term loops back to earlier gaps, but a learner finishing
      // mid-list keeps moving forward when there is a forward gap).
      const startIndex = state.activeTermIndex + 1;
      let nextIncompleteIndex = -1;
      for (let i = 0; i < totalTerms; i += 1) {
        const idx = (startIndex + i) % totalTerms;
        if (idx === state.activeTermIndex) continue;
        if (!isTermCompleted(terms[idx])) {
          nextIncompleteIndex = idx;
          break;
        }
      }

      if (nextIncompleteIndex >= 0) {
        return {
          ...state,
          activeTermIndex: nextIncompleteIndex,
          phase: 'learning',
          ...TERM_RESET_FIELDS,
          returnToResultsAfterReview: false,
          tutorChatOpen: false,
        };
      }

      return {
        ...state,
        phase: 'mastery',
        activeQuestionIndex: 0,
        masteryAnswers: [],
        masteryComplete: false,
        returnToResultsAfterReview: false,
        tutorChatOpen: false,
      };
    }

    case LESSON_VIEWER_MASTERY_SUBMIT:
      return {
        ...state,
        masteryAnswers: [
          ...state.masteryAnswers,
          {
            questionIndex: action.payload.questionIndex,
            selectedAnswer: action.payload.selectedAnswer,
            correct: action.payload.correct,
          },
        ],
        sessionStats: appendSessionAnswer(
          state.sessionStats,
          action.payload.correct,
          action.payload.timestamp,
        ),
      };

    case LESSON_VIEWER_MASTERY_NEXT: {
      const masteryQuestions =
        state.viewerModel?.mastery?.questions || [];
      const nextIdx = state.activeQuestionIndex + 1;
      if (nextIdx < masteryQuestions.length) {
        return {...state, activeQuestionIndex: nextIdx};
      }
      return {
        ...state,
        masteryComplete: true,
        phase: 'results',
        returnToResultsAfterReview: false,
        tutorChatOpen: false,
      };
    }

    case LESSON_VIEWER_START_TERM_REVIEW:
      return {
        ...state,
        activeTermIndex: action.payload,
        phase: 'learning',
        ...TERM_RESET_FIELDS,
        returnToResultsAfterReview: true,
        tutorChatOpen: false,
      };

    case LESSON_VIEWER_RETAKE_MASTERY:
      return {
        ...state,
        phase: 'mastery',
        activeQuestionIndex: 0,
        masteryAnswers: [],
        masteryComplete: false,
        returnToResultsAfterReview: false,
        tutorChatOpen: false,
      };

    case LESSON_VIEWER_TOGGLE_DEV_TOOLS:
      return {...state, showDevTools: !state.showDevTools};

    case LESSON_VIEWER_DEV_JUMP:
      return {
        ...state,
        ...TERM_RESET_FIELDS,
        masteryAnswers: [],
        masteryComplete: false,
        returnToResultsAfterReview: false,
        sessionStats: INITIAL_SESSION_STATS,
        tutorChatOpen: false,
        ...action.payload,
      };

    case LESSON_VIEWER_SET_TUTORIAL_VIEWED:
      return {...state, tutorialViewed: true, phase: 'frontloading'};

    case LESSON_VIEWER_SET_TUTOR_CHAT_OPEN:
      return {...state, tutorChatOpen: action.payload};

    case LESSON_VIEWER_UNITY_WORLD_LOADING_SET:
      return {...state, unityWorldLoading: action.payload};

    case LESSON_VIEWER_KEY_TERM_PROGRESS_SET:
      return {...state, keyTermProgress: action.payload || {}};

    default:
      return state;
  }
};

export default reducer;
